import { useEffect } from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';

import { ExportOrganization, WeekParams } from 'src/repositories/talents/TimeTrackingExportRepository';
import useTimeTrackingExportFile, { ExportFileStatus } from 'src/hooks/talents/useTimeTrackingExportFile';
import useModals from 'src/hooks/useModals';
import Box from 'src/components/Box';

import styles from './styles';

type ExportFileRowProps = {
  week: WeekParams;
  organization: ExportOrganization;
};

const FILE_BUTTON_LABELS: Record<ExportFileStatus, string> = {
  [ExportFileStatus.idle]: 'Preparing...',
  [ExportFileStatus.preparing]: 'Preparing...',
  [ExportFileStatus.ready]: 'Download',
  [ExportFileStatus.failed]: 'Try again',
};

export const ExportFileRow: React.FC<ExportFileRowProps> = props => {
  const { week, organization } = props;
  // a click saves a file that is already ready, so every download starts from a click
  const { status, prepare, save } = useTimeTrackingExportFile({ autoSave: false });
  const prepareFile = () => prepare({ ...week, organizationId: organization.id });

  useEffect(() => {
    prepareFile();
  }, []);

  const isPreparing = status === ExportFileStatus.idle || status === ExportFileStatus.preparing;

  const handleClick = () => {
    if (status === ExportFileStatus.failed) {
      prepareFile();
      return;
    }
    save();
  };

  return (
    <Stack component="li" sx={styles.row}>
      <Typography variant="body1">{organization.name}</Typography>
      <Button
        variant="contained"
        sx={styles.fileButton}
        disabled={isPreparing}
        startIcon={isPreparing && <CircularProgress size={14} color="inherit" />}
        onClick={handleClick}
        // every row has the same button, so its name says whose file it is
        aria-label={`${FILE_BUTTON_LABELS[status]} ${organization.name}`}
      >
        {FILE_BUTTON_LABELS[status]}
      </Button>
    </Stack>
  );
};

type TimeTrackingExportModalProps = WeekParams & {
  organizations: ExportOrganization[];
};

const TimeTrackingExportModal: React.FC<TimeTrackingExportModalProps> = props => {
  const { payPeriodAfter, payPeriodBefore, organizations } = props;
  const { hideModal } = useModals();
  const week = { payPeriodAfter, payPeriodBefore };

  return (
    <Box sx={styles.wrapper}>
      <Typography variant="h3" sx={styles.title}>
        Export Time Tracking
      </Typography>
      <Typography variant="body1">
        There is one file for each Organization. Click Download when a file is ready.
      </Typography>
      <Stack component="ul" sx={styles.list}>
        {organizations.map(organization => (
          <ExportFileRow key={organization.id} week={week} organization={organization} />
        ))}
      </Stack>
      <Box sx={styles.buttons}>
        <Button variant="outlined" sx={styles.closeButton} onClick={hideModal}>
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default TimeTrackingExportModal;
